/**
 * Language and Dialect Definitions
 * Contains metadata for all supported languages and dialects
 */

import { LanguageInfo, DialectInfo, SupportedLanguage } from "./types";

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: "en", name: "English", nativeName: "English", dir: "ltr", flag: "🇺🇸" },
  { code: "fr", name: "French", nativeName: "Français", dir: "ltr", flag: "🇫🇷" },
  { code: "es", name: "Spanish", nativeName: "Español", dir: "ltr", flag: "🇪🇸" },
  { code: "pt", name: "Portuguese", nativeName: "Português", dir: "ltr", flag: "🇧🇷" },
  { code: "ar", name: "Arabic", nativeName: "العربية", dir: "rtl", flag: "🇸🇦" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", dir: "ltr", flag: "🇮🇳" },
  { code: "zh", name: "Chinese (Simplified)", nativeName: "简体中文", dir: "ltr", flag: "🇨🇳" },
];

export const SUPPORTED_DIALECTS: DialectInfo[] = [
  { code: "tw", name: "Twi", nativeName: "Twi", region: "Ghana", flag: "🇬🇭" },
  { code: "ha", name: "Hausa", nativeName: "Hausa", region: "Nigeria/Niger", flag: "🇳🇬" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili", region: "East Africa", flag: "🇰🇪" },
  { code: "yo", name: "Yoruba", nativeName: "Yorùbá", region: "Nigeria", flag: "🇳🇬" },
  { code: "tl", name: "Filipino", nativeName: "Tagalog", region: "Philippines", flag: "🇵🇭" },
];

/**
 * Detect device language and map to supported language
 */
export function detectDeviceLanguage(): SupportedLanguage {
  if (typeof navigator === "undefined") return "en";
  
  const browserLang = navigator.language || (navigator as any).userLanguage || "en";
  const langCode = browserLang.split("-")[0].toLowerCase();
  
  // Map browser language to supported language
  const languageMap: Record<string, SupportedLanguage> = {
    en: "en",
    fr: "fr",
    es: "es",
    pt: "pt",
    ar: "ar",
    hi: "hi",
    zh: "zh",
    // Additional mappings for regional variants
    "pt-br": "pt",
    "zh-cn": "zh",
    "zh-tw": "zh",
    "es-mx": "es",
    "es-ar": "es",
    "fr-ca": "fr",
  };
  
  return languageMap[langCode] || languageMap[browserLang.toLowerCase()] || "en";
}

/**
 * Get language info by code
 */
export function getLanguageInfo(code: SupportedLanguage): LanguageInfo | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

/**
 * Get dialect info by code
 */
export function getDialectInfo(code: string): DialectInfo | undefined {
  return SUPPORTED_DIALECTS.find(dialect => dialect.code === code);
}
